import { VisionInstallSubStep, VisionPendingKind } from 'enums/installation';
import {
  IVisionDistanceInstallState,
  IVisionPendingConfirmationArgs,
} from 'interfaces/i-installation';
import {
  CALIBRATION_FRAME_INDICES,
  VisionCalibrationCameraType,
} from 'lib_ts/enums/vision.enums';
import {
  ICameraError,
  IOutOfSpecFrame,
  IVisionCameraCalibration,
  IVisionCameraResult,
} from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { isNil, uniqBy } from 'lodash';

const getCameraTypeOutOfSpecFrames = (
  error: ICameraError,
  type: VisionCalibrationCameraType
): IOutOfSpecFrame[] => {
  const { reprojection, cutoff } = error;
  const outOfSpecFrames: IOutOfSpecFrame[] = [];

  reprojection.forEach((v, i) => {
    if (v <= cutoff) {
      return;
    }

    outOfSpecFrames.push({
      type,
      value: v,
      index: CALIBRATION_FRAME_INDICES[type][i],
    });
  });

  return outOfSpecFrames.sort((a, b) => a.value - b.value);
};

export const getOutOfSpecFrames = (
  result: IVisionCameraResult
): IOutOfSpecFrame[] => {
  const leaderRetakeFrames = getCameraTypeOutOfSpecFrames(
    result.leader,
    VisionCalibrationCameraType.leader
  );

  const followerRetakeFrames = getCameraTypeOutOfSpecFrames(
    result.follower,
    VisionCalibrationCameraType.follower
  );

  const stereoRetakeFrames = getCameraTypeOutOfSpecFrames(
    result.stereo,
    VisionCalibrationCameraType.stereo
  );

  const uniqueOutOfSpecFrames = uniqBy(
    [leaderRetakeFrames, followerRetakeFrames, stereoRetakeFrames].flat(),
    (f) => f.index
  );

  return uniqueOutOfSpecFrames.sort((a, b) => b.value - a.value);
};

export const isDistancePendingConfirmation = (
  distance: IVisionDistanceInstallState,
  distancePendingComplete = false
): boolean => {
  if (distancePendingComplete) {
    return true;
  }

  return !isNil(distance.vcdFt) && distance.vcdComplete !== true;
};

export const isDistanceCalibrationComplete = (
  distance: IVisionDistanceInstallState,
  distancePendingComplete = false
): boolean => {
  if (isNil(distance.vcdFt) || distancePendingComplete) {
    return false;
  }

  return distance.vcdComplete === true;
};

export const getStoredPlateDistanceFt = (
  distance: IVisionDistanceInstallState,
  insetFt: number,
  distancePendingComplete = false
): number | undefined => {
  if (!isDistancePendingConfirmation(distance, distancePendingComplete)) {
    return undefined;
  }

  if (isNil(distance.vcdFt)) {
    return undefined;
  }

  return distance.vcdFt - insetFt;
};

export const isCameraPendingConfirmation = (
  vcc: IVisionCameraCalibration | undefined,
  isAdmin: boolean
): boolean => {
  if (!isAdmin) {
    return false;
  }

  if (!vcc?.board || !vcc.result || vcc.complete) {
    return false;
  }

  return getOutOfSpecFrames(vcc.result).length === 0;
};

export const getVisionPendingConfirmation = (
  args: IVisionPendingConfirmationArgs
): VisionPendingKind | null => {
  const { subStep, vcc, distance, distancePendingComplete, isAdmin } = args;

  const cameraPending = isCameraPendingConfirmation(vcc, isAdmin);
  const distancePending = isDistancePendingConfirmation(
    distance,
    distancePendingComplete
  );

  switch (subStep) {
    case VisionInstallSubStep.Camera: {
      return cameraPending ? VisionPendingKind.Camera : null;
    }

    case VisionInstallSubStep.Distance: {
      return distancePending ? VisionPendingKind.Distance : null;
    }

    case VisionInstallSubStep.Start: {
      if (cameraPending) {
        return VisionPendingKind.Camera;
      }

      if (distancePending) {
        return VisionPendingKind.Distance;
      }

      return null;
    }

    default: {
      return null;
    }
  }
};
